/**
 * TournamentDangerZoneCard — iter87.6. One card, all destructive
 * admin actions for THIS tournament:
 *
 *   - Lock crew picks (season pot pick'em)
 *   - Lock crew picks (golf pick'em)
 *   - Clear this tournament's draft
 *
 * Everything auto-hides when it doesn't apply (already-started
 * tournaments, tournaments without pot pick'em, or without a draft).
 * Pulling these out of the pick UIs / leaderboard strip keeps player
 * surfaces player-only.
 */
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Lock, Unlock, RotateCcw, Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { isGolfPickem } from "@/lib/tournamentFormat";
// iter87.180.9 — Swap window.confirm for BrutalConfirm on every async
// destructive path (iOS Safari silently drops native confirms).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

export default function TournamentDangerZoneCard({
    league, leagueId, tournament, isLocked, isFinal = false, onOpenComplete, onChanged,
}) {
    const tid = tournament?.id;
    // iter87.467 (user-reported — danger zone copy wrongly implied every
    // completed tournament gets a 45-day archive countdown) — only FREE
    // rivalries ever get the archive countdown armed (`apply_tournament_completion`,
    // 30 days, not 45); Pro+/paid tournaments stay available indefinitely.
    const tier = league?.subscription?.effective_tier || league?.subscription_tier || "free";
    const isProPlus = league?.pro_plus_ok === true || tier === "paid" || tier === "comp";
    const archiveCopy = isProPlus
        ? "This rivalry is Pro+, so it stays available — no auto-archive countdown."
        : "Since this is a free rivalry, this also closes it for everyone and starts the 30-day countdown to archive.";
    const isSeasonPot = tournament?.pickem?.mode === "pot";
    const isGolfPk = isGolfPickem(tournament);
    const seasonStarted = !!tournament?.round_started?.group;
    const golfR1Started = !!tournament?.golf?.r1_started;
    const hasDraftHere = !!(tid && league?.drafts_by_tournament?.[tid]);

    // Season pot pick'em admin lock — live state fetched so the button
    // reflects the CURRENT server-side flag (not a stale prop copy).
    const [seasonState, setSeasonState] = useState(null);
    const [busy, setBusy] = useState(false);

    // iter87.7 — Type-to-confirm gate for Clear Draft (same defense-in-
    // depth pattern the tournament completion dialog uses). Operator
    // must type "CLEAR" verbatim before the button un-disables.
    const [clearDialogOpen, setClearDialogOpen] = useState(false);
    const [clearConfirmText, setClearConfirmText] = useState("");
    const [clearing, setClearing] = useState(false);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    const loadState = useCallback(async () => {
        if (!tid || !isSeasonPot) return;
        try {
            const s = await api.getSeasonPickemState(tid);
            setSeasonState(s);
        } catch { /* best-effort */ }
    }, [tid, isSeasonPot]);
    useEffect(() => { loadState(); }, [loadState]);

    const seasonLocked = !!seasonState?.picks_locked_by_admin;
    const golfLocked = !!tournament?.golf?.picks_locked_by_admin;

    const toggleSeasonLock = async () => {
        if (isLocked) return;
        const locking = !seasonLocked;
        const ok = await brutalConfirm({
            title: locking ? "Lock crew picks?" : "Re-open crew picks?",
            body: locking
                ? "Every crewmate's picks freeze for the whole rivalry — they can no longer change until you re-open."
                : "Crewmates can edit their portfolios again.",
            confirmLabel: locking ? "Lock picks" : "Re-open",
            destructive: locking,
        });
        if (!ok) return;
        setBusy(true);
        try {
            const res = await api.lockSeasonPickemPicks(tid, locking);
            toast.success(res.picks_locked_by_admin ? "Picks locked for the crew" : "Picks re-opened");
            await loadState();
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Lock failed");
        }
        setBusy(false);
    };

    const toggleGolfLock = async () => {
        if (isLocked) return;
        const locking = !golfLocked;
        const ok = await brutalConfirm({
            title: locking ? "Lock crew picks?" : "Re-open crew picks?",
            body: locking
                ? "Every crewmate's golfers become visible and picks can no longer change."
                : "Golfer selections go back to hidden and crewmates can edit again.",
            confirmLabel: locking ? "Lock picks" : "Re-open",
            destructive: locking,
        });
        if (!ok) return;
        setBusy(true);
        try {
            await api.golfLockPicks(tid, locking);
            toast.success(locking ? "Picks locked — golfers revealed" : "Picks unlocked");
            onChanged?.({ force: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Lock change failed");
        }
        setBusy(false);
    };

    const clearDraft = () => {
        if (isLocked || !hasDraftHere) return;
        setClearConfirmText("");
        setClearDialogOpen(true);
    };

    const executeClearDraft = async () => {
        if (clearConfirmText.trim() !== "CLEAR") {
            toast.error("Type CLEAR to confirm");
            return;
        }
        setClearing(true);
        try {
            await api.clearDraft(leagueId, tid);
            toast.success("Draft cleared for this tournament");
            setClearDialogOpen(false);
            setClearConfirmText("");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to clear draft");
        }
        setClearing(false);
    };

    // If nothing dangerous applies, don't render at all.
    const showSeasonLock = isSeasonPot && !seasonStarted;
    const showGolfLock = isGolfPk && !golfR1Started;
    const showClearDraft = hasDraftHere;
    const showComplete = !!onOpenComplete && !isLocked;
    const showReopen = !!onOpenComplete && isLocked;
    if (!showSeasonLock && !showGolfLock && !showClearDraft && !showComplete && !showReopen) return null;

    return (
        <div className="brutal-card p-5 space-y-3 border-blaze/40" data-testid="tournament-danger-zone">
            <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-blaze" />
                <h2 className="font-display font-extrabold text-xl uppercase">Danger zone</h2>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
                Destructive admin actions for this tournament. Every action asks for confirmation before it fires. Anything below can create issues if you fire it by mistake — proceed carefully.
            </p>

            {/* iter87.9 — Mark Complete pulled up here so every one-way
                tournament action lives in one place. Uses the existing
                type-COMPLETE dialog owned by AdminPanel via onOpenComplete. */}
            {showComplete && isFinal && (
                <Row
                    testId="danger-zone-mark-complete-final"
                    title="Tournament is FINAL — every game has played"
                    sub={`Every match's result is in, but the tournament is still open for admin edits. Click Mark Complete to lock all edits (scores, teams, sync). ${archiveCopy} The Ride Story is already generated.`}
                    label="Mark complete"
                    Icon={Flag}
                    color="blaze"
                    disabled={isLocked}
                    onClick={onOpenComplete}
                />
            )}
            {showComplete && !isFinal && (
                <Row
                    testId="danger-zone-mark-complete"
                    title="Mark this tournament complete"
                    sub={`Locks every edit (scores, teams, advancement, drafts, scoring rules, live sync). Stops all upstream API calls for this tournament. ${archiveCopy}`}
                    label="Mark complete"
                    Icon={Flag}
                    color="blaze"
                    disabled={isLocked}
                    onClick={onOpenComplete}
                />
            )}
            {showReopen && (
                <div className="brutal-card-light p-3 flex items-center gap-3" data-testid="danger-zone-locked-info">
                    <Flag className="w-4 h-4 text-zinc-500 shrink-0" />
                    <div>
                        <p className="font-display font-extrabold text-sm uppercase">This tournament is complete</p>
                        <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
                            All edits are locked{isProPlus ? "" : " and the 30-day countdown to archive has started"}. Reopening isn't available to league admins — contact site support if this was locked by mistake.
                        </p>
                    </div>
                </div>
            )}

            {showSeasonLock && (
                <Row
                    testId="danger-zone-season-lock"
                    title="Lock crew picks"
                    sub={seasonLocked
                        ? "Currently locked — no crewmate can edit their portfolio."
                        : "Currently open — crewmates can still edit until first kickoff."}
                    label={seasonLocked ? "Re-open picks" : "Lock crew picks"}
                    Icon={seasonLocked ? Unlock : Lock}
                    color={seasonLocked ? "cyber" : "blaze"}
                    disabled={busy || isLocked}
                    onClick={toggleSeasonLock}
                />
            )}

            {showGolfLock && (
                <Row
                    testId="danger-zone-golf-lock"
                    title="Lock crew picks (golf)"
                    sub={golfLocked
                        ? "Currently locked — golfers revealed to the crew."
                        : "Currently open — golfer picks stay hidden until you lock or Round 1 tees off."}
                    label={golfLocked ? "Re-open picks" : "Lock crew picks"}
                    Icon={golfLocked ? Unlock : Lock}
                    color={golfLocked ? "cyber" : "blaze"}
                    disabled={busy || isLocked}
                    onClick={toggleGolfLock}
                />
            )}

            {showClearDraft && (
                <Row
                    testId="danger-zone-clear-draft"
                    title="Clear this tournament's draft"
                    sub="Wipes drafted teams for THIS tournament and emails recovery-enabled crewmates. Other tournaments in this rivalry are unaffected."
                    label="Clear draft"
                    Icon={RotateCcw}
                    color="heat"
                    disabled={isLocked}
                    onClick={clearDraft}
                />
            )}

            {/* iter87.7 — Type-to-confirm gate for Clear Draft. */}
            {clearDialogOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 px-4"
                    data-testid="clear-draft-confirm-dialog"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setClearDialogOpen(false);
                            setClearConfirmText("");
                        }
                    }}
                >
                    <div className="brutal-card p-6 max-w-md w-full bg-ink border-black space-y-4">
                        <div className="flex items-start gap-3">
                            <RotateCcw className="w-6 h-6 text-heat shrink-0 mt-1" />
                            <div>
                                <h3 className="font-display font-black text-xl uppercase">Clear this draft?</h3>
                                <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
                                    Every crewmate's drafted teams for "<strong className="text-zinc-200">{tournament?.name || "this tournament"}</strong>" will be wiped. Champion Bonus picks are preserved. Other tournaments in this rivalry are unaffected. This action can't be undone.
                                </p>
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">
                                Type <span className="text-heat">CLEAR</span> to confirm
                            </label>
                            <input
                                data-testid="clear-draft-confirm-input"
                                type="text"
                                value={clearConfirmText}
                                onChange={(e) => setClearConfirmText(e.target.value)}
                                className="brutal-input w-full"
                                placeholder="CLEAR"
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && clearConfirmText.trim() === "CLEAR") {
                                        executeClearDraft();
                                    }
                                }}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                data-testid="clear-draft-cancel-btn"
                                onClick={() => { setClearDialogOpen(false); setClearConfirmText(""); }}
                                className="brutal-btn-ghost text-xs"
                                disabled={clearing}
                            >
                                Cancel
                            </button>
                            <button
                                data-testid="clear-draft-confirm-btn"
                                onClick={executeClearDraft}
                                disabled={clearing || clearConfirmText.trim() !== "CLEAR"}
                                className="brutal-btn text-xs bg-heat text-white border-black shadow-brutal disabled:opacity-40"
                            >
                                {clearing ? "Clearing…" : "Clear draft"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {confirmHost}
        </div>
    );
}

function Row({ testId, title, sub, label, Icon, color, disabled, onClick }) {
    const btnClass = {
        blaze: "bg-blaze text-white",
        heat: "bg-heat text-white",
        cyber: "bg-cyber text-black",
    }[color] || "bg-heat text-white";
    return (
        <div className="brutal-card-light p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex-1 min-w-[12rem]">
                <p className="font-display font-extrabold text-sm uppercase">{title}</p>
                <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">{sub}</p>
            </div>
            <button
                data-testid={testId}
                onClick={onClick}
                disabled={disabled}
                className={`brutal-btn text-xs py-2 border-black shadow-brutal disabled:opacity-40 shrink-0 ${btnClass}`}
            >
                <Icon className="w-3.5 h-3.5" /> {label}
            </button>
        </div>
    );
}
